import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Heading, Link, PageLoader, Paragraph, Stack } from '@customerio/pluma-components/react';
import { AuthFormLayout } from '@/components/auth-form-layout';
import { hardRedirect } from '@/utils/hard-redirect';
import { isEmptyString } from '@/utils/string';
import { fetchJoinRequestAccounts, signupResumeHref, type JoinRequestAccounts } from '@/services/join-request/api';
import { OrgPickerStep } from './steps/org-picker-step';
import { RequestSentStep } from './steps/request-sent-step';

type JoinRequestPageProps = {
  token: string | undefined;
};

/**
 * Public "request to join your org" page, reached from the signup flow after
 * email verification when the verified email's domain matches an existing
 * account. The one-shot `token` query param authenticates every call; all its
 * failure modes (missing, expired, consumed) collapse into the link-expired
 * state, which points back at signup.
 */
export default function JoinRequestPage({ token: rawToken }: JoinRequestPageProps) {
  const [sent, setSent] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  // An empty `?token=` is as invalid as a missing one: normalize it away so
  // the page goes straight to the link-expired state instead of querying.
  const token = rawToken != null && !isEmptyString(rawToken) ? rawToken : undefined;

  const accountsQuery = useQuery({
    queryKey: ['join-request-accounts', token],
    queryFn: () => fetchJoinRequestAccounts(token ?? ''),
    enabled: token !== undefined && !sent,
    staleTime: Infinity,
    retry: false,
  });

  return (
    <AuthFormLayout>
      <JoinRequestContent
        token={token}
        sent={sent}
        tokenExpired={tokenExpired}
        accountsQuery={accountsQuery}
        onSent={() => setSent(true)}
        onTokenExpired={() => setTokenExpired(true)}
      />
    </AuthFormLayout>
  );
}

type JoinRequestContentProps = {
  token: string | undefined;
  sent: boolean;
  tokenExpired: boolean;
  accountsQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchJoinRequestAccounts>>>>;
  onSent: () => void;
  onTokenExpired: () => void;
};

/** The pre-data states: sent confirmation, no/expired token, loading, load error. */
function JoinRequestContent({
  token,
  sent,
  tokenExpired,
  accountsQuery,
  onSent,
  onTokenExpired,
}: JoinRequestContentProps) {
  if (sent) {
    return <RequestSentStep />;
  }

  if (token === undefined || tokenExpired) {
    return <LinkExpired />;
  }

  if (accountsQuery.isPending) {
    return <PageLoader label="Loading your organizations…" />;
  }

  if (accountsQuery.isError) {
    return (
      <Stack gap="200" data-test-join-request-error-state>
        <Heading level="1" size="md">
          Couldn't load organizations
        </Heading>
        <Paragraph color="subtle">We couldn't load your organizations. Refresh the page to try again.</Paragraph>
        <Link href="/signup">Continue creating a new account instead</Link>
      </Stack>
    );
  }

  return (
    <LoadedContent token={token} data={accountsQuery.data ?? null} onSent={onSent} onTokenExpired={onTokenExpired} />
  );
}

type LoadedContentProps = {
  token: string;
  /** Null when the token turned out invalid/expired/consumed (the fetch's 404 case). */
  data: JoinRequestAccounts | null;
  onSent: () => void;
  onTokenExpired: () => void;
};

/**
 * The post-load states, driven by the backend's match outcome: a single
 * resolved org (confirm + join), a tie we can't auto-connect (dead-end), org
 * churn (no match), or an invalid token. `match` is already narrowed to those
 * outcomes by normalizeMatch, so anything unactionable arrives here as
 * 'multiple' — the dead-end — never as no-match.
 */
function LoadedContent({ token, data, onSent, onTokenExpired }: LoadedContentProps) {
  if (data === null) {
    return <LinkExpired />;
  }

  if (data.match === 'multiple') {
    // Couldn't narrow to one org — don't ask the signup to pick between orgs
    // they may not recognize. Point them back to signup or a teammate invite.
    return (
      <Stack gap="200" data-test-join-request-multiple>
        <Heading level="1" size="md">
          Couldn't connect to your team
        </Heading>
        <Paragraph color="subtle">
          We couldn't automatically connect you to your team. Continue creating your account, or ask a teammate to
          invite you.
        </Paragraph>
        <Link href={signupResumeHref(data.email)}>Continue creating your account</Link>
      </Stack>
    );
  }

  if (data.match === 'single') {
    return <OrgPickerStep token={token} data={data} onSent={onSent} onTokenExpired={onTokenExpired} />;
  }

  // No org to join anymore (churn since mint, or an inconsistent response):
  // don't show a dead-end page — continue signup on the default path.
  return <ContinueSignup email={data.email} />;
}

type ContinueSignupProps = { email: string };

// Marks that we've already auto-continued this email back to signup. If the
// signup flow disagrees about the domain match and bounces back to
// /join-request, this stops a second auto-redirect so the two systems can't
// loop — the user gets a manual escape instead of an endless reload.
const AUTO_CONTINUE_KEY = 'joinRequest.autoContinued';

/**
 * sessionStorage access throws outright when storage is blocked for the origin
 * (private mode, partitioned embeds). /join-request is public and
 * unauthenticated, so an unguarded read during render would paint nothing at
 * all — same wrapping context/theme.tsx uses for its localStorage migration.
 */
function readAutoContinued(): string | null {
  try {
    return sessionStorage.getItem(AUTO_CONTINUE_KEY);
  } catch {
    return null;
  }
}

/**
 * Best-effort: a failed write costs at most one extra bounce, so it must not
 * stop the redirect that follows it — never leave the signup on the spinner.
 */
function markAutoContinued(email: string): void {
  try {
    sessionStorage.setItem(AUTO_CONTINUE_KEY, email);
  } catch {
    // Storage unavailable; the loop guard degrades, the redirect still happens.
  }
}

/**
 * Nothing to join, so there's no join UI to show — send the already-verified
 * signup straight back to finish creating their account on the normal path.
 * Guarded so a signup ↔ join-request disagreement can't loop.
 */
function ContinueSignup({ email }: ContinueSignupProps) {
  const alreadyContinued = readAutoContinued() === email;

  useEffect(() => {
    if (alreadyContinued) {
      return;
    }
    markAutoContinued(email);
    hardRedirect(signupResumeHref(email));
  }, [alreadyContinued, email]);

  // Only reached if we already redirected once and landed back here: give the
  // user a way out rather than an endless reload.
  if (alreadyContinued) {
    return (
      <Stack gap="200" data-test-join-request-no-match>
        <Heading level="1" size="md">
          Finish creating your account
        </Heading>
        <Paragraph color="subtle">
          There's no organization to join for your email domain — continue creating your account.
        </Paragraph>
        <Link href={signupResumeHref(email)}>Continue signing up</Link>
      </Stack>
    );
  }

  return <PageLoader label="Continuing signup…" />;
}

function LinkExpired() {
  return (
    <Stack gap="200" data-test-join-request-expired>
      <Heading level="1" size="md">
        Link expired
      </Heading>
      <Paragraph color="subtle">
        Join links are only valid for a short time after verifying your email. Start again from signup to get a new one.
      </Paragraph>
      <Link href="/signup">Back to signup</Link>
    </Stack>
  );
}
